import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import vendorBillService from '@api/v1/vendorBillService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'

const TABS = [
  { id: 'summary', label: 'AP Summary' },
  { id: 'memorized', label: 'Recurring Bills' },
]

/**
 * CC-53132: the only thing a read with no answer is allowed to render.
 *
 * "No AP data available" is a sentence about the firm's payables. It must be earned by
 * a read that finished. 'blocked' and 'failed' are the two ways of finishing with
 * nothing that mean the opposite — we do not know — and on an A/P screen that gap is a
 * missed payment: OVERDUE $0 and "no unpaid bills" are the same pixels.
 *
 * `role="alert"` because the panel already reads as an answer, and a retry because a
 * paused read never resolves by itself — `queryFn` was never called, so nothing is in
 * flight to finish.
 */
const ReadNotice = ({ state, title, children, onRetry }) => {
  const blocked = state === 'blocked'
  return (
    <div
      role="alert"
      className={`m-5 rounded-lg border p-4 text-sm ${
        blocked ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-100 bg-red-50 text-red-700'
      }`}
    >
      <p className="font-medium">{title}</p>
      <p className="mt-1">{children}</p>
      <button
        type="button"
        onClick={onRetry}
        className={`mt-3 rounded-lg border bg-white px-3 py-1.5 text-xs font-medium ${
          blocked
            ? 'border-amber-300 text-amber-900 hover:bg-amber-100'
            : 'border-red-200 text-red-700 hover:bg-red-100'
        }`}
      >
        Try again
      </button>
    </div>
  )
}

const VendorBills = () => {
  useDocumentTitle('Accounts Payable')
  const [activeTab, setActiveTab] = useState('summary')

  const summaryQuery = useQuery({
    queryKey: ['ap-summary'],
    queryFn: () => vendorBillService.getSummary(),
  })

  const memorizedQuery = useQuery({
    queryKey: ['ap-memorized'],
    queryFn: () => vendorBillService.getMemorized(),
    enabled: activeTab === 'memorized',
  })

  /*
   * CC-53132 — this page decided between "loading", "failed" and "empty" from
   * `isLoading` and an array length, and `isLoading` is `isPending && isFetching`, so
   * it is FALSE for a query that is pending but NOT in flight. The state that bites is
   * `fetchStatus: 'paused'`: with react-query's default `networkMode: 'online'` the
   * query does not fire while `onlineManager` believes the browser is offline.
   * `queryFn` is never called, nothing throws, nothing logs, `data` stays `undefined`,
   * and the `Array.isArray(...) ? ... : []` below turned that into an empty array.
   *
   * Rendered: **"No AP data available"** — under a heading that says "AP At a Glance",
   * with no spinner and no error, for as long as the browser stayed offline. There is
   * no worse sentence for this page to say wrongly. The A/P summary is where someone
   * checks what is OVERDUE, and its empty state and its no-answer state were the same
   * pixels.
   *
   * `useReadState` rather than the bare function so both reads carry the permanent
   * blocked-read diagnostic; a paused fetch leaves no other trace by construction.
   */
  const summaryState = useReadState('ap-summary', summaryQuery)
  const memorizedState = useReadState('ap-memorized', memorizedQuery)

  // Coerced only for the ready branches that iterate them. The `: []` fallbacks these
  // replace ran on every state, which is precisely how an unread response became a
  // statement about the firm's payables.
  const summary = Array.isArray(summaryQuery.data?.data) ? summaryQuery.data.data : []
  // The service has already shape-checked CB's envelope (spec 136): a wrong shape is
  // `failed` above, so an array here is the firm's actual list, possibly empty.
  const memorized = Array.isArray(memorizedQuery.data) ? memorizedQuery.data : []

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Accounts Payable</h1>
        <p className="text-sm text-gray-500 mt-1">Vendor bills, payments, and recurring expenses</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit" role="tablist" aria-label="AP tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'summary' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100" role="tabpanel" aria-label="AP Summary">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">AP At a Glance</h2>
          </div>
          {/* 'idle' rides with 'loading'. This query has no `enabled` option, so it
              cannot reach idle today — but every branch here names a state, and an
              unhandled fifth one renders NOTHING at all, i.e. a heading with a blank
              body for whoever adds an `enabled` gate later. Folding it in is safe
              precisely because it is unreachable: it can never become a permanent
              spinner. */}
          {summaryState === 'loading' || summaryState === 'idle' ? (
            <div className="p-8 flex justify-center"><LoadingSpinner message="Loading AP data..." /></div>
          ) : summaryState === 'blocked' ? (
            <ReadNotice
              state="blocked"
              title="Can’t reach the server, so we haven’t loaded your AP data."
              onRetry={() => summaryQuery.refetch()}
            >
              This is not a firm with nothing outstanding — no request was sent, so
              nothing here is describing what you owe. Your connection looks offline;
              check it and try again.
            </ReadNotice>
          ) : summaryState === 'failed' ? (
            /* The existing copy, kept, because it was already the right sentence —
               what it lacked was a way out and a statement of what it does NOT mean.
               A user who reads a bare failure next to an empty panel still walks away
               thinking there is nothing to pay. */
            <ReadNotice
              state="failed"
              title="Failed to load AP data"
              onRetry={() => summaryQuery.refetch()}
            >
              This is not a firm with nothing outstanding — nothing was read, so no
              amount and no bill count on this page is known.
            </ReadNotice>
          ) : summary.length === 0 ? (
            <p className="p-8 text-sm text-gray-500 text-center">No AP data available</p>
          ) : (
            <div className="p-5">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {summary.map((item, i) => {
                  const color = item.Title === 'OVERDUE' ? 'border-red-200 bg-red-50'
                    : item.Title === 'UNBILLED' ? 'border-amber-200 bg-amber-50'
                    : item.Title === 'PAID' ? 'border-green-200 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                  return (
                    <div key={i} className={`px-5 py-4 rounded-xl border ${color}`}>
                      <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider">{item.Title}</p>
                      <p className="text-2xl font-semibold text-gray-900 mt-1">{item.AmountString || `$${item.Amount}`}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{item.Records} bill{item.Records !== 1 ? 's' : ''}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'memorized' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100" role="tabpanel" aria-label="Recurring bills">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Recurring / Memorized Bills</h2>
          </div>
          {/* This read had no failure branch at all — every non-loading outcome fell
              into "No memorized bills found". A firm that believes it has no recurring
              bills stops expecting them to post.

              'idle' rides with 'loading' here for a different reason than on the
              summary above: this query IS conditionally enabled, but on exactly the
              condition that renders this panel (`activeTab === 'memorized'`), so it is
              enabled whenever these branches are on screen. Idle can only appear for
              the render in which the tab flips, and it cannot persist — so it cannot
              become a spinner that never stops. */}
          {memorizedState === 'loading' || memorizedState === 'idle' ? (
            <div className="p-8 flex justify-center"><LoadingSpinner message="Loading..." /></div>
          ) : memorizedState === 'blocked' || memorizedState === 'failed' ? (
            <ReadNotice
              state={memorizedState}
              title={memorizedState === 'blocked'
                ? 'Can’t reach the server, so we haven’t loaded your recurring bills.'
                : `Failed to load recurring bills: ${memorizedQuery.error?.message}`}
              onRetry={() => memorizedQuery.refetch()}
            >
              This is not a firm with no recurring bills — the list was never read, so
              anything set to post automatically is unaccounted for here, not absent.
            </ReadNotice>
          ) : memorized.length === 0 ? (
            <p className="p-8 text-sm text-gray-500 text-center">No memorized bills found</p>
          ) : (
            <div className="p-5">
              <pre className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 overflow-x-auto max-h-96">
                {JSON.stringify(memorized, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default VendorBills
